import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eraser } from "lucide-react";
import { api, formatApiErrorDetail, fmtDateUS } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;
const METHODS = ["Check", "Cash", "Direct deposit", "Zelle", "Venmo", "Other"];

export const PaymentsPanel = () => {
  const today = () => new Date().toISOString().slice(0, 10);
  const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [report, setReport] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", amount: "", paid_date: today(), method: "Check", note: "" });
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState("");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [annual, setAnnual] = useState(null);
  const [cleanupDate, setCleanupDate] = useState(today());
  const [cleanupPrev, setCleanupPrev] = useState(null);
  const [cleanupEmps, setCleanupEmps] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const loadAnnual = useCallback(() => {
    api.get("/admin/payments/annual", { params: { year } }).then(({ data }) => setAnnual(data)).catch(() => {});
  }, [year]);
  useEffect(() => { loadAnnual(); }, [loadAnnual]);
  useEffect(() => {
    const refresh = () => loadAnnual();
    window.addEventListener("payments-changed", refresh);
    return () => window.removeEventListener("payments-changed", refresh);
  }, [loadAnnual]);

  const downloadAnnual = async () => {
    setDlBusy("annual");
    try {
      const { data } = await api.get("/admin/payments/annual.pdf", { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payments-annual-${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Annual PDF downloaded — ready to print");
    } catch {
      toast.error("Could not build the annual report");
    } finally {
      setDlBusy("");
    }
  };

  const load = useCallback(() => {
    api.get("/admin/payments/report", { params: { from_date: from, to_date: to } }).then(({ data }) => setReport(data)).catch(() => {});
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const previewCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.get("/admin/payroll/cleanup-preview", { params: { keep_date: cleanupDate } });
      setCleanupPrev(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not load the cleanup preview");
    } finally {
      setCleanupBusy(false);
    }
  };

  const runCleanup = async () => {
    if (!cleanupPrev) return;
    const msg = `Delete ${cleanupPrev.entries_delete} time entr${cleanupPrev.entries_delete === 1 ? "y" : "ies"} and ${cleanupPrev.payments_delete} payment${cleanupPrev.payments_delete === 1 ? "" : "s"}${cleanupEmps && cleanupPrev.test_employees ? `, plus ${cleanupPrev.test_employees} TEST crew member${cleanupPrev.test_employees === 1 ? "" : "s"}` : ""}?\n\nEverything entered on ${fmtDateUS(cleanupDate)} stays (${cleanupPrev.entries_keep} entries, ${cleanupPrev.payments_keep} payments). This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/payroll/cleanup", { keep_date: cleanupDate, remove_test_employees: cleanupEmps });
      toast.success(`Cleanup done — deleted ${data.deleted_entries} entries, ${data.deleted_payments} payments${data.removed_test_employees ? `, ${data.removed_test_employees} TEST crew` : ""}. Kept everything from ${fmtDateUS(cleanupDate)}.`);
      setCleanupPrev(null);
      window.dispatchEvent(new Event("payments-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("payments-changed", refresh);
    return () => window.removeEventListener("payments-changed", refresh);
  }, [load]);
  useEffect(() => { api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {}); }, []);

  const savePayment = async () => {
    if (!form.employee_id) return toast.error("Pick the worker you paid");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter the amount paid");
    setBusy(true);
    try {
      await api.post("/admin/payments", { ...form, amount });
      toast.success("Payment saved to the ledger");
      setForm({ ...form, amount: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const voidPayment = async (p) => {
    const extra = p.source === "payroll" ? " The hours go back to unpaid so you can redo payroll." : "";
    if (!window.confirm(`Void the $${p.amount.toFixed(2)} payment to ${p.employee_name} from ${fmtDateUS(p.paid_date)}?${extra}`)) return;
    try {
      await api.delete(`/admin/payments/${p.id}`);
      toast.success("Payment voided");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const download = async (ext, mime) => {
    setDlBusy(ext);
    try {
      const { data } = await api.get(`/admin/payments/report.${ext}`, { params: { from_date: from, to_date: to }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payments-${from}-to-${to}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} report downloaded`);
    } catch {
      toast.error("Could not build the report");
    } finally {
      setDlBusy("");
    }
  };

  return (
    <div className="border border-primary/40 bg-primary/5 rounded-xl p-5 space-y-4" data-testid="payments-panel">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">Payments ledger — every payment saved, reported month by month</p>

      <div className="space-y-2" data-testid="payment-record-form">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Record a payment by hand — bonus, adjustment, cash</p>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="payment-employee" className={selectCls}>
            <option value="" className="bg-[#252720]">Worker…</option>
            {employees.map((e) => <option key={e.id} value={e.id} className="bg-[#252720]">{e.name}</option>)}
          </select>
          <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount $" data-testid="payment-amount" className={inputCls} />
          <input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} data-testid="payment-date" className={inputCls} />
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method" className={selectCls}>
            {METHODS.map((m) => <option key={m} value={m} className="bg-[#252720]">{m}</option>)}
          </select>
        </div>
        {employees.length === 0 && <p className="text-[11px] text-amber-300/80" data-testid="payment-no-workers">No active crew yet — add workers in the General tab first.</p>}
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional) — e.g. bonus for the Johnson job" data-testid="payment-note" className={inputCls} />
        <button onClick={savePayment} disabled={busy} data-testid="payment-save" className="flex items-center justify-center gap-2 w-full border border-dashed border-white/25 rounded-lg py-3 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 disabled:opacity-50">
          <Plus size={13} aria-hidden="true" /> {busy ? "Saving..." : "Save payment"}
        </button>
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3" data-testid="payments-report">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Monthly report — pick the exact dates you need</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className={inputCls} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className={inputCls} />
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("csv", "text/csv")} disabled={!!dlBusy} data-testid="report-csv" className="flex-1 border border-white/20 text-white/70 font-bold uppercase tracking-widest text-[11px] py-3 rounded-lg hover:border-primary hover:text-primary transition-colors duration-300 disabled:opacity-50">
            {dlBusy === "csv" ? "Building..." : "CSV"}
          </button>
          <button onClick={() => download("pdf", "application/pdf")} disabled={!!dlBusy} data-testid="report-pdf" className="flex-1 border border-white/20 text-white/70 font-bold uppercase tracking-widest text-[11px] py-3 rounded-lg hover:border-primary hover:text-primary transition-colors duration-300 disabled:opacity-50">
            {dlBusy === "pdf" ? "Building..." : "PDF"}
          </button>
        </div>

        {report && report.months.length === 0 && <p className="text-[11px] text-white/35" data-testid="report-empty">No payments in this date range yet — mark a payroll period as paid or record one above.</p>}
        {report && report.months.map((m) => (
          <div key={m.month} className="border border-white/10 rounded-lg overflow-hidden" data-testid={`report-month-${m.month}`}>
            <div className="flex items-center justify-between px-3 py-2 bg-white/5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">{m.label} · {m.count} payment{m.count !== 1 ? "s" : ""}</span>
              <span className="text-sm font-bold text-primary" data-testid={`report-month-total-${m.month}`}>${m.total.toFixed(2)}</span>
            </div>
            {m.employees.map((e) => (
              <div key={e.name} className="flex items-center justify-between px-3 py-1.5 border-t border-white/5 text-xs" data-testid={`report-emp-${m.month}-${e.name.replace(/[^a-z0-9]+/gi, "-")}`}>
                <span className="font-semibold">{e.name} <span className="text-white/35 text-[10px]">×{e.count}</span></span>
                <span className="font-bold text-white/90">${e.total.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-white/10 px-3 py-2 space-y-1.5">
              {m.payments.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-[11px] text-white/55" data-testid={`payment-row-${p.id}`}>
                  <span className="flex-1 min-w-0 truncate">
                    {fmtDateUS(p.paid_date)} · {p.employee_name} · <span className="text-white/85 font-semibold">${p.amount.toFixed(2)}</span>
                    {p.method ? ` · ${p.method}` : ""}
                    <span className={`ml-1.5 text-[8px] font-bold uppercase tracking-widest ${p.source === "payroll" ? "text-primary" : "text-white/40"}`}>{p.source === "payroll" ? `payroll ${fmtDateUS(p.period_from)}–${fmtDateUS(p.period_to)}` : "manual"}</span>
                    {p.note ? ` · ${p.note}` : ""}
                  </span>
                  <button onClick={() => voidPayment(p)} aria-label={`Void payment ${p.id}`} data-testid={`payment-void-${p.id}`} className="text-red-400/70 hover:text-red-300 p-1 shrink-0"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {report && report.months.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2.5 border border-primary/40 rounded-lg bg-primary/10 text-sm font-bold" data-testid="report-grand-total">
            <span className="uppercase tracking-widest text-[10px] text-white/60">Total paid {fmtDateUS(from)} – {fmtDateUS(to)}</span>
            <span className="text-primary">${report.grand_total.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3" data-testid="annual-section">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Annual totals — tax season</p>
          <button onClick={downloadAnnual} disabled={!!dlBusy} data-testid="annual-pdf" className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline disabled:opacity-50">
            {dlBusy === "annual" ? "Building..." : "Print / PDF"}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/50 shrink-0">Year:</span>
          <input type="number" min="2020" max="2100" step="1" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || new Date().getFullYear())} data-testid="annual-year" className={`${inputCls} !w-28`} />
        </div>
        {annual && annual.workers.length === 0 && <p className="text-[11px] text-white/35" data-testid="annual-empty">No payments recorded in {annual.year}.</p>}
        {annual && annual.workers.length > 0 && (
          <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="annual-table">
            <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-white/5 text-[9px] font-bold uppercase tracking-widest text-white/40">
              <span className="col-span-5">Worker</span>
              <span className="col-span-2 text-right">Payments</span>
              <span className="col-span-2 text-right">Hours</span>
              <span className="col-span-3 text-right">Total paid</span>
            </div>
            {annual.workers.map((w) => (
              <div key={w.name} className="border-t border-white/5 px-3 py-2" data-testid={`annual-row-${w.name.replace(/[^a-z0-9]+/gi, "-")}`}>
                <div className="grid grid-cols-12 gap-1 text-xs items-center">
                  <span className="col-span-5 font-semibold truncate">{w.name}</span>
                  <span className="col-span-2 text-right text-white/60">{w.count}</span>
                  <span className="col-span-2 text-right text-white/60">{w.hours > 0 ? `${w.hours}h` : "—"}</span>
                  <span className="col-span-3 text-right font-bold text-white/90">${w.total.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-white/35 mt-1 truncate">{w.months.map((m) => `${m.label} $${m.total.toFixed(0)}`).join(" · ")}</p>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-1 px-3 py-2.5 border-t border-white/15 bg-white/5 text-xs font-bold items-center" data-testid="annual-grand-total">
              <span className="col-span-5 uppercase tracking-widest text-[10px] text-white/60">Everyone in {annual.year}</span>
              <span className="col-span-2 text-right text-white/70">{annual.count}</span>
              <span className="col-span-2" />
              <span className="col-span-3 text-right text-primary">${annual.grand_total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-red-400/20 pt-4 space-y-3" data-testid="payroll-cleanup">
        <p className="text-[10px] font-bold uppercase tracking-widest text-red-300/60">Clean up payroll data</p>
        <p className="text-[11px] text-white/45 leading-relaxed">
          Deletes every time entry and payment NOT entered on the day you pick — everything entered that day stays.
          Use this to wipe test clutter. Check the preview first; deleting cannot be undone.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/50 shrink-0">Keep what I entered on:</span>
          <input type="date" value={cleanupDate} onChange={(e) => { setCleanupDate(e.target.value); setCleanupPrev(null); }} data-testid="cleanup-keep-date" className={inputCls} />
        </div>
        <label className="flex items-center gap-2 text-[11px] text-white/55 cursor-pointer" data-testid="cleanup-emps-label">
          <input type="checkbox" checked={cleanupEmps} onChange={(e) => setCleanupEmps(e.target.checked)} data-testid="cleanup-emps" className="accent-red-400" />
          Also remove deactivated TEST-named crew members
        </label>
        <button onClick={previewCleanup} disabled={cleanupBusy || !cleanupDate} data-testid="cleanup-preview" className="flex items-center justify-center gap-2 w-full border border-white/20 rounded-lg py-3 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 disabled:opacity-50">
          <Eraser size={13} /> {cleanupBusy && !cleanupPrev ? "Checking..." : "Preview cleanup"}
        </button>
        {cleanupPrev && (
          <div className="border border-red-400/30 bg-red-400/5 rounded-lg p-3 space-y-2" data-testid="cleanup-preview-result">
            <p className="text-[11px] text-white/70" data-testid="cleanup-counts">
              Will delete <span className="font-bold text-red-300">{cleanupPrev.entries_delete} time entries</span> and <span className="font-bold text-red-300">{cleanupPrev.payments_delete} payments</span>
              {cleanupEmps && cleanupPrev.test_employees > 0 && <> plus <span className="font-bold text-red-300">{cleanupPrev.test_employees} TEST crew members</span></>}
              {" "}— keeping <span className="font-bold text-primary">{cleanupPrev.entries_keep} entr{cleanupPrev.entries_keep === 1 ? "y" : "ies"}</span> and <span className="font-bold text-primary">{cleanupPrev.payments_keep} payment{cleanupPrev.payments_keep === 1 ? "" : "s"}</span> entered on {fmtDateUS(cleanupDate)}.
            </p>
            {cleanupPrev.entries_delete === 0 && cleanupPrev.payments_delete === 0 && (!cleanupEmps || cleanupPrev.test_employees === 0) ? (
              <p className="text-[11px] text-white/40" data-testid="cleanup-nothing">Nothing to delete — payroll is already clean.</p>
            ) : (
              <button onClick={runCleanup} disabled={cleanupBusy} data-testid="cleanup-confirm" className="w-full bg-red-500/90 hover:bg-red-500 text-white rounded-lg py-3 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 disabled:opacity-50">
                {cleanupBusy ? "Deleting..." : "Delete now — keep only that day"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
